import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ThreadMessage, ThreadsBatchParams, useThreadsBatch } from '@screentone/addon-auth-wrapper';
import { Wrapper } from '@screentone/core';

import { ResponsiveLoader } from 'components';
import { getPageLockThreadId } from 'contexts/page-lock/PageLockContext';
import { CollectionDto, CollectionsDtoInput, useInfiniteCollectionsDtoQuery } from 'data/generated/graphql';
import styles from './CollectionSearch.module.scss';
import { CollectionsList } from './components/collections-list/CollectionsList';
import { CollectionsSearchBar, Filter } from './components/collections-search-bar/CollectionsSearchBar';
import { useConvertedProperty } from '../../hooks';

const INITIAL_THREAD_IDS: string[] = [];
const INITIAL_THREAD_DATA: Record<string, ThreadMessage[]> = {};

interface CollectionSearchProps {
  emptyListMessage?: string;
  filters?: Filter[];
  initialCollectionsInput?: Partial<CollectionsDtoInput>;
  renderCollectionCardWrapper?: (
    collectionCard: ReactElement,
    collection: CollectionDto,
    index: number
  ) => ReactElement;
  resultListClassName?: string;
  excludedCollectionIdCreatedUtcs?: string[];
  droppableId?: string;
  /** render this jsx before rendering list of content */
  beforeContent?: React.ReactNode;
}

const PER_PAGE = 10;

export const CollectionSearch = ({
  emptyListMessage,
  filters,
  initialCollectionsInput,
  renderCollectionCardWrapper,
  resultListClassName,
  excludedCollectionIdCreatedUtcs,
  droppableId,
  beforeContent = null
}: CollectionSearchProps) => {
  const { property } = useParams<{ property: string }>();
  const currentPropertyFromAuth = useConvertedProperty();

  // Use property from URL params as the source of truth, with auth as fallback
  // This ensures we have the correct property immediately after navigation
  const currentProperty = property === 'wsjcommerce' ? 'buyside' : property ?? currentPropertyFromAuth;

  const [collectionsInput, setCollectionsInput] = useState<CollectionsDtoInput>({
    publicationKey: currentProperty ?? '',
    ...initialCollectionsInput
  });

  useEffect(() => {
    if (!currentProperty) {
      return;
    }

    setCollectionsInput((collectionsInput) => ({ ...collectionsInput, publicationKey: currentProperty }));
  }, [currentProperty]);

  const enabled = !!collectionsInput.publicationKey && collectionsInput.publicationKey.length > 0;
  const {
    data,
    isLoading: isLoad,
    fetchStatus,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteCollectionsDtoQuery(
    {
      collectionsDTOInput: {
        ...collectionsInput,
        ...(excludedCollectionIdCreatedUtcs ? { excludedCollectionIdCreatedUtcs } : {})
      },
      paginationParams: { first: PER_PAGE, after: null }
    },
    {
      enabled,
      getNextPageParam: (lastPage) => {
        if (!lastPage.collectionsDTO.pageInfo.hasNextPage) return undefined;
        return {
          paginationParams: {
            first: PER_PAGE,
            after: lastPage.collectionsDTO.pageInfo.endCursor
          }
        };
      }
    }
  );
  const isLoading = isLoad && fetchStatus !== 'idle';

  const [threadsData, setThreadsData] = useState<Record<string, ThreadMessage[]>>(INITIAL_THREAD_DATA);
  const fetchedThreadIdsRef = useRef<Set<string>>(new Set());

  // Only batch-fetch thread data for collections we haven't already fetched, so that
  // scrolling to load more pages doesn't re-query the ids we already have.
  const newThreadIds = useMemo(() => {
    if (!data) return INITIAL_THREAD_IDS;

    return data.pages.flatMap((page) =>
      page.collectionsDTO.edges
        .map((edge) => getPageLockThreadId(edge.node.metadata.allessehId))
        .filter((threadId) => !fetchedThreadIdsRef.current.has(threadId))
    );
  }, [data]);

  const batchParams: ThreadsBatchParams = useMemo(
    () => ({ threadIds: newThreadIds, limit: 1, sort: 'DESC' }),
    [newThreadIds]
  );
  const { threadsData: newThreadsData, loading: threadsLoading } = useThreadsBatch(batchParams);

  useEffect(() => {
    // Wait for the batch to resolve before marking its ids as fetched - otherwise a
    // page loaded while this batch is still in flight would exclude these (still
    // unresolved) ids from the next batch, and they'd never get queried again.
    if (newThreadIds.length === 0 || threadsLoading) return;

    newThreadIds.forEach((threadId) => fetchedThreadIdsRef.current.add(threadId));
    setThreadsData((prevThreadsData) => ({ ...prevThreadsData, ...newThreadsData }));
  }, [newThreadIds, newThreadsData, threadsLoading]);

  const handlePartialCollectionsInputChange = (newSearchParams: Partial<CollectionsDtoInput>) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setCollectionsInput((collectionsInput) => ({ ...collectionsInput, ...newSearchParams }));
  };

  const nextPageProps = { fetchNextPage, hasNextPage, isFetchingNextPage };

  return (
    <>
      {filters && (
        <CollectionsSearchBar
          collectionsInput={collectionsInput}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          onPartialCollectionsInputChange={handlePartialCollectionsInputChange}
          filters={filters}
        />
      )}
      <Wrapper className={styles.collectionsListWrapper} data-testid="collection-search-container">
        {beforeContent}
        {isLoading ? (
          <ResponsiveLoader />
        ) : (
          <>
            <CollectionsList
              className={resultListClassName}
              collectionPages={data?.pages ?? []}
              emptyListMessage={emptyListMessage}
              renderCollectionCardWrapper={renderCollectionCardWrapper}
              droppableId={droppableId}
              nextPageProps={nextPageProps}
              initialThreadData={threadsData}
            />
          </>
        )}
      </Wrapper>
    </>
  );
};
